import React, { useEffect, useRef, useState } from "react";
import { completeNewPassword, isCognitoConfigured, signIn } from "./auth/cognito";
import landingBackground from "./assets/landing.jpg";

/**
 * Unauthenticated landing page: full-bleed cover photo with a sign-in modal.
 * Standalone-only — inside New Relic the platform handles identity.
 */
export default function LandingPage({ onAuthenticated }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="landing"
      style={{ backgroundImage: `url(${landingBackground})` }}
    >
      <div className="landing__scrim" />

      <main className="landing__content">
        <p className="landing__eyebrow">RMP</p>
        <h1 className="landing__title">Health Dashboard</h1>
        <p className="landing__subtitle">
          Real-time operational health across every market, store and service.
        </p>
        <button
          type="button"
          className="landing__cta"
          onClick={() => setOpen(true)}
        >
          Sign in
        </button>
      </main>

      {open ? (
        <SignInModal
          onClose={() => setOpen(false)}
          onAuthenticated={onAuthenticated}
        />
      ) : null}
    </div>
  );
}

function SignInModal({ onClose, onAuthenticated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [challengeUser, setChallengeUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [challengeUser]);

  // Escape to dismiss, and keep focus inside the dialog while it is open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button, input, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const configured = isCognitoConfigured();

  const handleSignIn = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.status === "NEW_PASSWORD_REQUIRED") {
        setChallengeUser(result.user);
        setPassword("");
      } else {
        onAuthenticated(result.session);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleNewPassword = async (event) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const result = await completeNewPassword(challengeUser, newPassword);
      onAuthenticated(result.session);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="signin__overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="signin"
        role="dialog"
        aria-modal="true"
        aria-labelledby="signin-title"
        ref={dialogRef}
      >
        <button
          type="button"
          className="signin__close"
          onClick={onClose}
          aria-label="Close sign in"
        >
          &#x2715;
        </button>

        {challengeUser ? (
          <>
            <h2 id="signin-title" className="signin__title">Choose a new password</h2>
            <p className="signin__lede">
              This account still has a temporary password. Set a permanent one to
              continue.
            </p>

            <form onSubmit={handleNewPassword}>
              <label className="signin__label" htmlFor="new-password">
                New password
              </label>
              <input
                id="new-password"
                ref={firstFieldRef}
                className="signin__input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />

              <label className="signin__label" htmlFor="confirm-password">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                className="signin__input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />

              {error ? <p className="signin__error" role="alert">{error}</p> : null}

              <button type="submit" className="signin__submit" disabled={busy}>
                {busy ? "Saving…" : "Set password and continue"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 id="signin-title" className="signin__title">Sign in</h2>
            <p className="signin__lede">
              Use your Health Dashboard account to continue.
            </p>

            {!configured ? (
              <p className="signin__notice" role="alert">
                Sign-in is not configured for this deployment. Set{" "}
                <code>cognito.userPoolId</code> and <code>cognito.clientId</code>{" "}
                in <code>config.json</code>.
              </p>
            ) : null}

            <form onSubmit={handleSignIn}>
              <label className="signin__label" htmlFor="email">Email</label>
              <input
                id="email"
                ref={firstFieldRef}
                className="signin__input"
                type="email"
                autoComplete="username"
                spellCheck="false"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={!configured}
                required
              />

              <label className="signin__label" htmlFor="password">Password</label>
              <input
                id="password"
                className="signin__input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={!configured}
                required
              />

              {error ? <p className="signin__error" role="alert">{error}</p> : null}

              <button
                type="submit"
                className="signin__submit"
                disabled={busy || !configured}
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
